import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, Prompt } from "react-router-dom";
import { Steps, Button, Spin, notification, Modal, Result } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  ProfileOutlined,
  PictureOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import "./AddMMHProduct.scss";
import DetailsStep from "./steps/DetailsStep";
import ImagesStep from "./steps/ImagesStep";
import ReviewStep from "./steps/ReviewStep";
import {
  AddMMHProduct as AddMMHProductAPI,
  UpdateMMHProduct,
  GetMMHProductById,
  GetProjectDataByCode,
  GetLocationByPincode,
  GetMasters,
} from "../MMHProductsServices";
import {
  FALLBACK_CATEGORIES,
  FALLBACK_CATEGORY_TYPES,
  FALLBACK_STYLES,
  IMAGE_CATEGORY_OPTIONS,
  MAX_IMAGES_PER_PRODUCT,
  ROOM_FIELD_NAMES,
  isBhkProjectType,
} from "../constants";
import { imageSrcOf } from "../imageUrl";
import {
  buildProjectName,
  cascadeImageDetails,
  deriveAreas,
  ensureSingleDp,
  getDpImage,
  imagesStepHasErrors,
  inheritableFrom,
  serialiseRoomCounts,
  validateDetailsStep,
  validateImagesStep,
  validateUploadFile,
} from "../utils";

const LIST_ROUTE = "/home/ap/mmh-products/list";

// Exactly the fields the legacy add/edit form posted — nothing added,
// nothing dropped.
const EMPTY_FORM = {
  projectCode: "",
  projectName: "",
  width: "",
  length: "",
  floor: "",
  plotArea: "",
  buildUpArea: "",
  staircaseType: "",
  vastuPriority: "",
  basement: "",
  direction: "",
  clientLocation: "",
  clientName: "",
  pincode: "",
  category: "",
  categoryType: "",
  projectType: "",
  bedRoom: "",
  livingRoom: "",
  drawingRoom: "",
  kitchen: "",
  diningRoom: "",
  bathroom: "",
  projectCost: "",
  productCode: "",
  planType: "",
  parking: 0,
  description: "",
  addLink: "",
};

const EMPTY_ROOM_COUNTS = ROOM_FIELD_NAMES.reduce((acc, name) => {
  acc[name] = "";
  return acc;
}, {});

const pickRoomCounts = (form) =>
  ROOM_FIELD_NAMES.reduce((acc, name) => {
    acc[name] = form[name];
    return acc;
  }, {});

const STEPS = [
  { title: "Product Details", icon: <ProfileOutlined /> },
  { title: "Images & Tagging", icon: <PictureOutlined /> },
  { title: "Review & Save", icon: <CheckCircleOutlined /> },
];

const FALLBACK_MASTERS = {
  categories: FALLBACK_CATEGORIES,
  categoryTypes: FALLBACK_CATEGORY_TYPES,
  styles: FALLBACK_STYLES.map((name, i) => ({ id: i + 1, name })),
  imageCategories: IMAGE_CATEGORY_OPTIONS,
};

const newUid = () => `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const AddMMHProduct = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const history = useHistory();

  const [form, setForm] = useState(EMPTY_FORM);
  const [customFields, setCustomFields] = useState([{ id: "cf-1", name: "", value: "" }]);
  const [imageItems, setImageItems] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [masters, setMasters] = useState(FALLBACK_MASTERS);

  const [step, setStep] = useState(0);
  const [detailErrors, setDetailErrors] = useState({});
  const [imageErrors, setImageErrors] = useState({ form: {}, images: {} });

  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [loadFailed, setLoadFailed] = useState(false);
  const [autoFillState, setAutoFillState] = useState({ loading: false, code: "" });
  const [pincodeState, setPincodeState] = useState({ loading: false });

  const [nameTouched, setNameTouched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const justSavedRef = useRef(false);

  const objectUrlsRef = useRef(new Set());
  const lastCodeRef = useRef("");
  const lastPincodeRef = useRef("");
  // The project type and room counts as saved, so switching back to the
  // saved type in edit mode brings its numbers back.
  const savedRoomsRef = useRef(null);
  // antd fires beforeUpload once per file in the same tick, so a multi-file
  // drop would read the same stale length every time.
  const imageCountRef = useRef(0);

  const registerObjectUrl = (url) => {
    objectUrlsRef.current.add(url);
    return url;
  };
  const releaseObjectUrl = (url) => {
    if (url && objectUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      objectUrlsRef.current.delete(url);
    }
  };

  useEffect(() => {
    imageCountRef.current = imageItems.length;
  }, [imageItems.length]);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  // ---- Masters ----------------------------------------------------------
  // Category, category type and style live in database masters; the panel
  // reads them instead of carrying its own copy.

  useEffect(() => {
    let cancelled = false;
    GetMasters()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? {};
        setMasters({
          categories: data.categories?.length ? data.categories : FALLBACK_MASTERS.categories,
          categoryTypes: data.categoryTypes?.length ? data.categoryTypes : FALLBACK_MASTERS.categoryTypes,
          styles: data.styles?.length ? data.styles : FALLBACK_MASTERS.styles,
          imageCategories: data.imageCategories?.length ? data.imageCategories : FALLBACK_MASTERS.imageCategories,
        });
      })
      .catch(() => {
        // Dropdowns keep working off the fallback lists.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryName = useMemo(() => {
    const match = masters.categories.find((c) => String(c.id) === String(form.category));
    return match ? match.name : "";
  }, [masters.categories, form.category]);

  // ---- Load (edit mode) -------------------------------------------------

  useEffect(() => {
    if (!isEditMode) return undefined;
    let cancelled = false;

    GetMMHProductById(id)
      .then((res) => {
        if (cancelled) return;
        const product = res?.data?.data || res?.data || {};

        const next = { ...EMPTY_FORM };
        Object.keys(EMPTY_FORM).forEach((key) => {
          if (product[key] !== undefined && product[key] !== null) {
            next[key] = product[key];
          }
        });
        setForm(next);
        setNameTouched(true);
        savedRoomsRef.current = {
          projectType: next.projectType,
          rooms: pickRoomCounts(next),
        };

        setCustomFields(
          product.customFields?.length
            ? product.customFields.map((f, i) => ({
                id: f.id ?? `cf-${i}`,
                name: f.name ?? "",
                value: f.value ?? "",
              }))
            : [{ id: "cf-1", name: "", value: "" }]
        );

        const existing = (product.images || []).map((img, index) => ({
          uid: String(img.id ?? `existing-${index}`),
          id: img.id ?? null,
          file: null,
          fileName: img.fileName ?? "",
          preview: imageSrcOf(img),
          title: img.title ?? "",
          category: img.category ?? "",
          subCategory: img.subCategory ?? "",
          otherCategory: img.otherCategory ?? "",
          style: img.style ?? "",
          keywords: Array.isArray(img.keywords) ? img.keywords : [],
          description: img.description ?? "",
          isDp: Boolean(img.isDp),
          // Already saved, so it never inherits from a neighbour.
          touched: true,
        }));
        setImageItems(ensureSingleDp(existing));

        if (product.projectCode) lastCodeRef.current = product.projectCode;
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  // ---- Derived ----------------------------------------------------------

  useEffect(() => {
    const { plotArea, buildUpArea } = deriveAreas(form);
    setForm((prev) =>
      prev.plotArea === plotArea && prev.buildUpArea === buildUpArea
        ? prev
        : { ...prev, plotArea, buildUpArea }
    );
  }, [form.width, form.length, form.floor]); // eslint-disable-line react-hooks/exhaustive-deps

  const dpImageCategory = getDpImage(imageItems)?.category || "";
  const suggestedName = useMemo(
    () => buildProjectName(form, categoryName, dpImageCategory),
    [form, categoryName, dpImageCategory]
  );

  useEffect(() => {
    if (nameTouched || !suggestedName) return;
    setForm((prev) => (prev.projectName === suggestedName ? prev : { ...prev, projectName: suggestedName }));
  }, [suggestedName, nameTouched]);

  // ---- Field handlers ---------------------------------------------------

  const markDirty = () => setIsDirty(true);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setDetailErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  // The room columns hold room counts for a residential project and BHK unit
  // counts for every other type. Crossing between the two leaves the old
  // numbers meaning something else, so they are cleared — or, in edit mode,
  // put back to the saved ones when returning to the saved kind of type.
  const handleProjectTypeChange = useCallback((value) => {
    setForm((prev) => {
      const next = { ...prev, projectType: value };
      if (isBhkProjectType(prev.projectType) === isBhkProjectType(value)) return next;

      const saved = savedRoomsRef.current;
      const restore = saved && isBhkProjectType(saved.projectType) === isBhkProjectType(value);
      return { ...next, ...(restore ? saved.rooms : EMPTY_ROOM_COUNTS) };
    });
    setIsDirty(true);
    setDetailErrors((prev) => (prev.projectType ? { ...prev, projectType: undefined } : prev));
  }, []);

  const handleInput = useCallback(
    (event) => {
      const { name, value } = event.target;
      if (name === "projectName") setNameTouched(true);
      setField(name, value);
    },
    [setField]
  );

  const handleApplySuggestedName = () => {
    setField("projectName", suggestedName);
    setNameTouched(false);
  };

  // ---- Lookups ----------------------------------------------------------

  const handleLookupProjectCode = async () => {
    const code = form.projectCode?.trim();
    if (!code || code === lastCodeRef.current) return;
    lastCodeRef.current = code;

    setAutoFillState({ loading: true, code: "" });
    try {
      const res = await GetProjectDataByCode(code);
      const project = res?.data?.project || res?.project || res?.data;
      if (!project || !Object.keys(project).length) throw new Error("not found");

      setForm((prev) => ({
        ...prev,
        width: project.width != null ? String(project.width) : prev.width,
        length: project.depth != null ? String(project.depth) : prev.length,
        floor: project.floor != null ? String(project.floor) : prev.floor,
        // Both come back in the shape the columns store: a numeric staircase
        // type and a two-letter direction code.
        staircaseType: project.staircaseType != null ? Number(project.staircaseType) : prev.staircaseType,
        vastuPriority: project.vastuPriority ?? prev.vastuPriority,
        direction: project.direction || prev.direction,
      }));
      setAutoFillState({ loading: false, code });
      setIsDirty(true);
      notification.success({
        message: "Project found",
        description: `Dimensions & Structure filled from ${code}. You can still edit any field.`,
        placement: "bottomRight",
      });
    } catch (error) {
      setAutoFillState({ loading: false, code: "" });
      notification.warning({
        message: "Project not found",
        description: `No project details for "${code}". Fill the section below manually.`,
        placement: "bottomRight",
      });
    }
  };

  const handleLookupPincode = async () => {
    const pincode = form.pincode?.trim();
    if (!pincode || !/^\d{6}$/.test(pincode) || pincode === lastPincodeRef.current) return;
    lastPincodeRef.current = pincode;

    setPincodeState({ loading: true });
    try {
      const res = await GetLocationByPincode(pincode);
      const location = res?.data?.location || "";
      if (!location) throw new Error("not found");

      setForm((prev) => ({ ...prev, clientLocation: location }));
      setDetailErrors((prev) => ({ ...prev, clientLocation: undefined, pincode: undefined }));
      setIsDirty(true);
    } catch (error) {
      notification.warning({
        message: "Could not resolve pincode",
        description: "Type the client location manually.",
        placement: "bottomRight",
      });
    } finally {
      setPincodeState({ loading: false });
    }
  };

  // ---- Custom fields ----------------------------------------------------

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { id: `cf-${Date.now()}`, name: "", value: "" }]);
    markDirty();
  };
  const updateCustomField = (fieldId, key, value) => {
    setCustomFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, [key]: value } : f)));
    markDirty();
  };
  const removeCustomField = (fieldId) => {
    setCustomFields((prev) => {
      const next = prev.filter((f) => f.id !== fieldId);
      return next.length ? next : [{ id: `cf-${Date.now()}`, name: "", value: "" }];
    });
    markDirty();
  };

  // ---- Images -----------------------------------------------------------

  const addImageFile = (file) => {
    const problem = validateUploadFile(file);
    if (problem) {
      notification.error({ message: "File rejected", description: problem, placement: "bottomRight" });
      return;
    }
    if (imageCountRef.current >= MAX_IMAGES_PER_PRODUCT) {
      notification.warning({
        message: "Image limit reached",
        description: `A product can hold at most ${MAX_IMAGES_PER_PRODUCT} images.`,
        placement: "bottomRight",
      });
      return;
    }
    imageCountRef.current += 1;

    const preview = registerObjectUrl(URL.createObjectURL(file));

    setImageItems((prev) => {
      const blank = {
        uid: newUid(),
        id: null,
        file,
        fileName: file.name,
        preview,
        title: "",
        category: "",
        subCategory: "",
        otherCategory: "",
        style: "",
        keywords: [],
        description: "",
        isDp: false,
        touched: false,
      };

      // Inherit from the last image that already has details, so a new photo
      // arrives pre-filled instead of empty.
      const donor = [...prev].reverse().find((item) => item.touched);
      const seeded = donor ? { ...blank, ...inheritableFrom(donor), isDp: false } : blank;

      // ensureSingleDp promotes this one automatically when it is the first.
      return ensureSingleDp([...prev, seeded]);
    });
    markDirty();
  };

  /**
   * Editing an image marks it as the admin's own work and pushes its details
   * down onto every later image they have not touched yet.
   */
  const updateImageItem = (uid, patch) => {
    setImageItems((prev) => {
      const index = prev.findIndex((item) => item.uid === uid);
      if (index === -1) return prev;

      const next = [...prev];
      next[index] = { ...next[index], ...patch, touched: true };

      return cascadeImageDetails(next, index);
    });

    setImageErrors((prev) => {
      if (!prev.images?.[uid]) return prev;
      const nextImages = { ...prev.images };
      delete nextImages[uid];
      return { ...prev, images: nextImages };
    });
    markDirty();
  };

  /** Explicit "copy the image above" for when inheritance was skipped. */
  const copyFromPrevious = (uid) => {
    setImageItems((prev) => {
      const index = prev.findIndex((item) => item.uid === uid);
      if (index <= 0) return prev;

      const next = [...prev];
      next[index] = { ...next[index], ...inheritableFrom(prev[index - 1]), touched: true };
      return next;
    });
    markDirty();
    notification.success({
      message: "Details copied",
      description: "Copied from the image above. Edit anything that differs.",
      placement: "bottomRight",
    });
  };

  const removeImageItem = (uid) => {
    const removed = imageItems.find((item) => item.uid === uid);
    if (!removed) return;

    if (removed.file) releaseObjectUrl(removed.preview);
    if (removed.id) {
      setDeletedImageIds((ids) => (ids.includes(removed.id) ? ids : [...ids, removed.id]));
    }

    setImageItems((prev) => ensureSingleDp(prev.filter((item) => item.uid !== uid)));
    markDirty();
  };

  const setDpImage = (uid) => {
    setImageItems((prev) => ensureSingleDp(prev.map((item) => ({ ...item, isDp: item.uid === uid }))));
    setImageErrors((prev) => ({ ...prev, form: { ...prev.form, dp: undefined } }));
    markDirty();
  };

  const moveImage = (from, to) => {
    setImageItems((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return ensureSingleDp(next);
    });
    markDirty();
  };

  // ---- Steps ------------------------------------------------------------

  const validateStep = (index) => {
    if (index === 0) {
      const errors = validateDetailsStep(form);
      setDetailErrors(errors);
      return Object.keys(errors).length === 0;
    }
    if (index === 1) {
      const errors = validateImagesStep(imageItems);
      setImageErrors(errors);
      return !imagesStepHasErrors(errors);
    }
    return true;
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goToStep = (target) => {
    if (target === step) return;
    if (target < step) {
      setStep(target);
      scrollToTop();
      return;
    }
    for (let index = step; index < target; index += 1) {
      if (!validateStep(index)) {
        setStep(index);
        scrollToTop();
        notification.warning({
          message: "Some details need attention",
          description: "Fix the highlighted fields before moving on.",
          placement: "bottomRight",
        });
        return;
      }
    }
    setStep(target);
    scrollToTop();
  };

  // ---- Submit -----------------------------------------------------------

  const buildPayload = () => ({
    ...form,
    ...serialiseRoomCounts(form),
    parking: form.parking ? 1 : 0,
    projectType: Number(form.projectType) || 0,
    staircaseType: Number(form.staircaseType) || 0,
    customFields: customFields
      .filter((f) => f.name?.trim())
      .map((f) => ({ name: f.name.trim(), value: f.value?.trim() ?? "" })),
    deletedImageIds,
    images: imageItems.map((item, index) => ({
      id: item.id,
      file: item.file,
      fileName: item.fileName,
      url: item.preview,
      title: item.title.trim(),
      category: item.category,
      subCategory: item.subCategory,
      otherCategory: item.otherCategory?.trim() ?? "",
      style: item.style,
      keywords: item.keywords,
      description: item.description?.trim() ?? "",
      isDp: item.isDp,
      position: index,
    })),
  });

  const handleSubmit = async () => {
    if (!validateStep(0)) {
      setStep(0);
      scrollToTop();
      notification.error({
        message: "Product details are incomplete",
        description: "Fix the highlighted fields and try again.",
        placement: "bottomRight",
      });
      return;
    }
    if (!validateStep(1)) {
      setStep(1);
      scrollToTop();
      notification.error({
        message: "Image details are incomplete",
        description: "Every image needs a title, category and style.",
        placement: "bottomRight",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditMode) {
        await UpdateMMHProduct(id, payload);
      } else {
        await AddMMHProductAPI(payload);
      }

      justSavedRef.current = true;
      setIsDirty(false);
      notification.success({
        message: isEditMode ? "Product updated" : "Product created",
        description: "The product and all of its image details were saved together.",
        placement: "bottomRight",
      });
      history.push(LIST_ROUTE);
    } catch (error) {
      notification.error({
        message: "Could not save this product",
        description:
          error?.response?.data?.message ||
          "Something went wrong while saving. Please try again.",
        placement: "bottomRight",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isDirty) {
      history.push(LIST_ROUTE);
      return;
    }
    Modal.confirm({
      title: "Discard your changes?",
      content: "This product has not been saved yet. Leaving now loses everything you have entered.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: () => {
        setIsDirty(false);
        justSavedRef.current = true;
        history.push(LIST_ROUTE);
      },
    });
  };

  // ---- Render -----------------------------------------------------------

  if (pageLoading) {
    return (
      <div className="mmh-page-loader">
        <Spin size="large" tip="Loading product..." />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <Result
        status="404"
        title="Product not found"
        subTitle="This MMH product could not be loaded. It may have been deleted."
        extra={<Button type="primary" onClick={() => history.push(LIST_ROUTE)}>Back to list</Button>}
      />
    );
  }

  return (
    <div className="add-mmh-product">
      <Prompt
        when={isDirty && !justSavedRef.current}
        message="You have unsaved changes. Leave this page and lose them?"
      />

      <div className="mmh-page-title">
        <span>{isEditMode ? "Edit MMH Product" : "Add MMH Product"}</span>
        <span className="mmh-page-subtitle">
          {isEditMode
            ? "Update the details and images, then save — everything is written together"
            : "Fill in the details, tag the images, then save — nothing is written until the last step"}
        </span>
      </div>

      <div className="mmh-card">
        <Steps current={step} className="mmh-steps" onChange={goToStep} size="small">
          {STEPS.map((s) => (
            <Steps.Step key={s.title} title={s.title} icon={s.icon} />
          ))}
        </Steps>

        <div className="mmh-step-body">
          {step === 0 && (
            <DetailsStep
              form={form}
              errors={detailErrors}
              masters={masters}
              setField={setField}
              handleInput={handleInput}
              onProjectTypeChange={handleProjectTypeChange}
              customFields={customFields}
              autoFillState={autoFillState}
              pincodeState={pincodeState}
              suggestedName={suggestedName}
              onLookupProjectCode={handleLookupProjectCode}
              onLookupPincode={handleLookupPincode}
              onAddCustomField={addCustomField}
              onUpdateCustomField={updateCustomField}
              onRemoveCustomField={removeCustomField}
              onApplySuggestedName={handleApplySuggestedName}
            />
          )}

          {step === 1 && (
            <ImagesStep
              imageItems={imageItems}
              errors={imageErrors}
              masters={masters}
              onAddFile={addImageFile}
              onUpdateImage={updateImageItem}
              onRemoveImage={removeImageItem}
              onSetDp={setDpImage}
              onMoveImage={moveImage}
              onCopyFromPrevious={copyFromPrevious}
            />
          )}

          {step === 2 && (
            <ReviewStep
              form={form}
              categoryName={categoryName}
              customFields={customFields}
              imageItems={imageItems}
              onGoToStep={goToStep}
            />
          )}
        </div>

        <div className="mmh-submit-row">
          <Button className="mmh-cancel-btn" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>

          <div className="mmh-submit-row-right">
            {step > 0 && (
              <Button icon={<ArrowLeftOutlined />} onClick={() => goToStep(step - 1)} disabled={saving}>
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button className="mmh-submit-btn" onClick={() => goToStep(step + 1)}>
                Next <ArrowRightOutlined />
              </Button>
            ) : (
              <Button className="mmh-submit-btn" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                {isEditMode ? "Update Product" : "Save Product"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMMHProduct;
